import React, {useState} from 'react';

import * as AnalysisActions from 'analysis-tool/AnalysisActions';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {refreshAnalysis} from 'analysis-tool/polling/refreshPipeline';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import {ParentStepReference, StepReference} from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL, PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import useFormInput from 'ui-library/hooks/useFormInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';

export const FRONTEND_MAX_COMMENT_LENGTH = 1500;

export interface FlagRowsModalProps {
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onFinalizeFlagging?: () => void;
  analysis: PipelineGraphQL;
  selectedRowIds: Set<string>;
  flaggedRowIds: Set<string>;
  isAddingFlag: boolean; // if false we clear the flags
  parentStepReference: ParentStepReference;
}

export function openFlagRowsModal(
  flagRowsModalProps: Omit<FlagRowsModalProps, 'onRequestClose' | 'onRequestSubmit'>
): void {
  legacyRunModal(FlagRowsModal, flagRowsModalProps);
}

function assertAndConvertToInteger(value: string): number {
  const maybeNumber = parseInt(value, 10);
  assert(!Number.isNaN(maybeNumber), `Expected a number but got ${value}`);
  return maybeNumber;
}

export function FlagRowsModal(props: FlagRowsModalProps): JSX.Element {
  const commentFormInput = useFormInput('');
  const comment = commentFormInput.value;
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const reasonOptions = props.isAddingFlag
    ? BenchlingConfig.ANALYSIS_TOOL_FLAGGING_REASON_OPTIONS
    : BenchlingConfig.ANALYSIS_TOOL_UNFLAGGING_REASON_OPTIONS;
  const hasReasonOptions = reasonOptions.length > 0;

  const hasValidComment = comment && comment.match(/^\s*$/) == null;
  const canSubmit = hasValidComment && (!hasReasonOptions || selectedReason != null);
  // We do not want to add a remove flags comment on rows that are not previously flagged
  const selectedRowIdArray = props.isAddingFlag
    ? [...props.selectedRowIds]
    : [...props.selectedRowIds].filter((selectedRowId) => props.flaggedRowIds.has(selectedRowId));

  async function handleSubmit(): Promise<void> {
    const rowIndices = selectedRowIdArray.map((selectedRowId: string) =>
      assertAndConvertToInteger(selectedRowId)
    );
    const flaggingInfo = {
      isFlagged: props.isAddingFlag,
      reason: hasReasonOptions ? selectedReason : null,
      comment,
      rowIndices,
    };
    assert(
      props.parentStepReference.stepReference.type === StepReference.STEP_ID,
      'Expected StepId in FlagRowsModal'
    );
    const parentStepId = props.parentStepReference.stepReference.stepId;
    const parentStep = getLegacyStepFromAnalysis(props.analysis, parentStepId);

    props.onRequestSubmit();

    if (parentStep.definition.stepType === PipelineStepType.FLAG_ROWS) {
      const previousStepConfig = getPipelineStepConfig(parentStep, PipelineStepType.FLAG_ROWS, 0);
      const stepConfig = {
        flaggingInfoList: [...previousStepConfig.flaggingInfoList, flaggingInfo],
      };
      await withLoadingScreen(
        AnalysisActions.updateAndRunAnalysisStep(props.analysis.id, {stepId: parentStep.id, stepConfig})
      );
    } else {
      const parentStepData = props.analysis.steps
        .find((step) => step.id === parentStepId)
        ?.outputStepDatas.find(
          (outputStepData) => outputStepData.name === props.parentStepReference.stepDataName
        );
      assert(parentStepData != null, 'Expected to find parent output step data referenced by FlagRowsModal');
      const stepConfig: PipelineStepConfig<PipelineStepType.FLAG_ROWS, 0> = {
        flaggingInfoList: [flaggingInfo],
      };
      await withLoadingScreen(
        addStepWithInferredEdges(props.analysis, {
          stepType: PipelineStepType.FLAG_ROWS,
          stepData: parentStepData,
          stepDataIndex: props.parentStepReference.fromStepDataIndex,
          config: stepConfig,
          runAfterCreate: true,
        })
      );
      await withLoadingScreen(refreshAnalysis(props.analysis.id));
    }

    props.onFinalizeFlagging?.();
  }

  const flagNoun = pluralize(selectedRowIdArray.length, 'flag', 'flags');
  const rowNoun = pluralize(selectedRowIdArray.length, 'row', 'rows');
  return (
    <Modal
      title={
        props.isAddingFlag
          ? `Add flag to ${selectedRowIdArray.length} ${rowNoun}`
          : `Remove ${selectedRowIdArray.length} ${flagNoun}`
      }
      buttons={[{style: ButtonStyle.SECONDARY, submit: true, text: 'Save', disabled: !canSubmit}]}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={handleSubmit}
      expandHeight={true}
      dataTestAttrs={{component: 'FlagRowsModal', element: 'root'}}
    >
      {!props.isAddingFlag && <p>{`Write a comment to remove ${flagNoun}.`}</p>}
      {hasReasonOptions && (
        <FormGroup>
          <InputWrapper id="flagRowsModalReason" label="Reason" labelStyle={FieldLabel.STYLES.LABEL}>
            <SelectDropdown
              displayName={selectedReason ?? 'Select a reason'}
              menu={
                <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL}>
                  {reasonOptions.map((reason: string) => (
                    <Option
                      key={reason}
                      onSelect={() => {
                        setSelectedReason(reason);
                      }}
                      value={reason}
                      dataTestAttrs={{
                        component: 'FlagRowsModal',
                        element: 'reasonOption',
                        key: reason,
                      }}
                    >
                      {reason}
                    </Option>
                  ))}
                </Menu>
              }
              dataTestAttrs={{
                component: 'FlagRowsModal',
                element: 'reasonDropdown',
              }}
            />
          </InputWrapper>
        </FormGroup>
      )}
      <FormGroup>
        <InputWrapper id="flagRowsModalComment" label="Comment" labelStyle={FieldLabel.STYLES.LABEL}>
          <textarea
            maxLength={FRONTEND_MAX_COMMENT_LENGTH}
            onChange={(e) => {
              // @ts-expect-error
              commentFormInput.onChange(e);
            }}
            rows={2}
            value={comment}
          />
        </InputWrapper>
      </FormGroup>
    </Modal>
  );
}
